import React, { forwardRef } from 'react';
import {
  TextInput,
  TextInputProps,
  View,
  StyleProp,
  ViewStyle,
  Platform,
} from 'react-native';
import { Bevel } from './Bevel';
import { Text } from './Text';
import { makeStyles, useTheme } from './ThemeContext';

export interface TextFieldProps extends TextInputProps {
  label?: string;
  containerStyle?: StyleProp<ViewStyle>;
  /** Renders the label above rather than inline to its left. */
  labelAbove?: boolean;
}

/** A sunken Win95 edit control. Forwards its ref to the underlying TextInput
 *  so forms can move focus from one field to the next. */
export const TextField = forwardRef<TextInput, TextFieldProps>(function TextField(
  { label, containerStyle, labelAbove, style, editable = true, ...rest },
  ref
) {
  const styles = useStyles();
  const theme = useTheme();
  const field = (
    <Bevel
      variant="sunken"
      background={editable ? theme.colors.fieldBg : theme.colors.face}
      style={[styles.field, labelAbove || !label ? styles.fieldFull : styles.fieldInline]}
      contentStyle={styles.fieldInner}
    >
      <TextInput
        ref={ref}
        style={[styles.input, style]}
        editable={editable}
        placeholderTextColor={theme.colors.disabledText}
        underlineColorAndroid="transparent"
        // Win95 fields had no autocorrect, no smart quotes, no capitalization.
        autoCorrect={false}
        autoCapitalize="none"
        spellCheck={false}
        accessibilityLabel={label}
        {...rest}
      />
    </Bevel>
  );

  if (!label) {
    return <View style={containerStyle}>{field}</View>;
  }

  return (
    <View style={[labelAbove ? styles.rowAbove : styles.rowInline, containerStyle]}>
      <Text style={labelAbove ? styles.labelAbove : styles.labelInline}>{label}</Text>
      {field}
    </View>
  );
});

const useStyles = makeStyles((t) => ({
  rowInline: { flexDirection: 'row' as const, alignItems: 'center' as const },
  rowAbove: { flexDirection: 'column' as const },
  labelInline: { marginRight: 6 },
  labelAbove: { marginBottom: 2 },
  field: { height: Math.max(20, t.metrics.touchTarget - 12) },
  fieldFull: { alignSelf: 'stretch' as const },
  fieldInline: { flex: 1 },
  fieldInner: { justifyContent: 'center' as const, paddingHorizontal: 3 },
  input: {
    flex: 1,
    fontFamily: t.fonts.ui,
    fontSize: t.fontSizes.ui,
    color: t.colors.windowText,
    padding: 0,
    margin: 0,
    // RN on macOS/Windows draws a focus ring these themes never had.
    ...Platform.select({ macos: { outlineWidth: 0 }, windows: { outlineWidth: 0 }, default: {} }),
  },
}));
